import { STORAGE } from '../constants'
import type { MediaType } from '../types'

/* ============================================================
   Site configuration — what the owner of the TV decides.

   This is deliberately separate from Settings. Settings holds a
   viewer's preferences (server, add-ons, subtitle language, HDR).
   Config decides what the app *is*: which destinations exist, which
   rows appear and in what order, which features are available at
   all, and how it looks. The admin interface edits only this.
   ============================================================ */

export type SectionId = 'home' | 'movies' | 'series' | 'search' | 'library' | 'settings'

export const SECTION_LABELS: Record<SectionId, string> = {
  home: 'Home',
  movies: 'Movies',
  series: 'Series',
  search: 'Search',
  library: 'My library',
  settings: 'Settings',
}

export interface FeatureFlags {
  subtitles: boolean
  audioTracks: boolean
  trailers: boolean
  cast: boolean
  similar: boolean
  showAllSources: boolean
  smallerAlternative: boolean
  sourceRisks: boolean
  startupHealthCheck: boolean
  onScreenKeyboard: boolean
  watchedMarkers: boolean
  nextEpisodeCard: boolean
  sleepTimer: boolean
  playbackSpeed: boolean
  zoomToggle: boolean
  resumePrompt: boolean
  phoneRemote: boolean
  hdrDiagnostics: boolean
}

export const FEATURE_LABELS: Record<keyof FeatureFlags, { title: string; detail: string }> = {
  subtitles: { title: 'Subtitles', detail: 'Fetch and render subtitle tracks during playback.' },
  audioTracks: { title: 'Audio tracks', detail: 'Let viewers switch between audio tracks in a file.' },
  trailers: { title: 'Trailers', detail: 'Offer a trailer on the details page.' },
  cast: { title: 'Cast', detail: 'List the leading cast on the details page.' },
  similar: { title: 'More like this', detail: 'Suggest related titles on the details page.' },
  showAllSources: { title: 'Show all sources', detail: 'Allow viewers past the recommended sources to the full list.' },
  smallerAlternative: { title: 'Smaller alternative', detail: 'Offer a lighter file alongside each recommendation.' },
  sourceRisks: { title: 'Playback warnings', detail: 'Flag sources this TV may not decode.' },
  startupHealthCheck: { title: 'Stream health check', detail: 'Measure the connection before playing and warn about stalls.' },
  onScreenKeyboard: { title: 'On-screen keyboard', detail: 'Use the grid keyboard for search instead of the system one.' },
  watchedMarkers: { title: 'Watched markers', detail: 'Tick episodes that have been finished.' },
  nextEpisodeCard: { title: 'Next episode', detail: 'Offer the next episode as the credits start.' },
  sleepTimer: { title: 'Sleep timer', detail: 'Allow playback to stop itself after a set time.' },
  playbackSpeed: { title: 'Playback speed', detail: 'Expose speed control in the player.' },
  zoomToggle: { title: 'Zoom and fill', detail: 'Allow switching aspect handling for wide films.' },
  resumePrompt: { title: 'Resume where left off', detail: 'Continue part-watched titles from their saved position. Off means everything starts from the beginning.' },
  phoneRemote: { title: 'Phone remote', detail: 'Allow a paired phone to control this TV.' },
  hdrDiagnostics: { title: 'HDR diagnostics', detail: 'Show the panel capability readout in Settings and allow the hardware test screen.' },
}

export interface NavItemConfig {
  id: SectionId
  enabled: boolean
}

export type RowKind = 'continue' | 'nextUp' | 'newThisWeek' | 'recommended' | 'catalog' | 'watchlist' | 'genre'

export interface RowConfig {
  id: string
  title: string
  kind: RowKind
  type?: MediaType
  genre?: string
  sort?: 'popular' | 'rating' | 'year'
  limit: number
  enabled: boolean
}

export const ROW_KIND_LABELS: Record<RowKind, string> = {
  continue: 'Continue watching',
  nextUp: 'Next episode in a series',
  newThisWeek: 'Released in the last week',
  recommended: 'Based on watch history',
  catalog: 'Catalogue',
  watchlist: 'Saved to my list',
  genre: 'Single genre',
}

export interface HeroConfig {
  enabled: boolean
  rotate: boolean
  intervalSeconds: number
  source: 'movie' | 'series' | 'mixed'
  count: number
  showDescription: boolean
}

export type CaptionMode = 'focus' | 'always'
export type RailLabels = 'expand' | 'always' | 'never'

export interface AppearanceConfig {
  accent: string
  postersPerRow: number
  captionMode: CaptionMode
  animations: boolean
  showClock: boolean
  showServerStatus: boolean
  safeArea: number
  focusScale: number
  dimSiblings: boolean
}

export type CodecPolicy = 'strict' | 'balanced' | 'any'

export interface PlaybackConfig {
  seekStep: number
  bigSeekStep: number
  maxFileSizeGb: number
  codecPolicy: CodecPolicy
  chromeHideSeconds: number
}

export interface AdminConfig {
  version: number
  pin: string
  features: FeatureFlags
  nav: NavItemConfig[]
  rows: RowConfig[]
  hero: HeroConfig
  appearance: AppearanceConfig
  playback: PlaybackConfig
}

export const CONFIG_VERSION = 1

export const DEFAULT_CONFIG: AdminConfig = {
  version: CONFIG_VERSION,
  pin: '',
  features: {
    subtitles: true,
    audioTracks: true,
    trailers: true,
    cast: true,
    similar: true,
    showAllSources: true,
    smallerAlternative: true,
    sourceRisks: true,
    startupHealthCheck: true,
    onScreenKeyboard: true,
    watchedMarkers: true,
    nextEpisodeCard: true,
    sleepTimer: true,
    playbackSpeed: true,
    zoomToggle: true,
    resumePrompt: true,
    phoneRemote: true,
    hdrDiagnostics: true,
  },
  nav: [
    { id: 'home', enabled: true },
    { id: 'movies', enabled: true },
    { id: 'series', enabled: true },
    { id: 'search', enabled: true },
    { id: 'library', enabled: true },
    { id: 'settings', enabled: true },
  ],
  rows: [
    { id: 'continue', title: 'Continue watching', kind: 'continue', limit: 20, enabled: true },
    { id: 'next-up', title: 'Next up', kind: 'nextUp', limit: 20, enabled: true },
    { id: 'new-this-week', title: 'New this week', kind: 'newThisWeek', limit: 18, enabled: true },
    { id: 'recommended', title: 'Because you watched', kind: 'recommended', limit: 18, enabled: true },
    { id: 'trending-movies', title: 'Trending movies', kind: 'catalog', type: 'movie', sort: 'popular', limit: 18, enabled: true },
    { id: 'top-series', title: 'Binge-worthy series', kind: 'catalog', type: 'series', sort: 'popular', limit: 18, enabled: true },
    { id: 'watchlist', title: 'From my list', kind: 'watchlist', limit: 18, enabled: false },
    { id: 'top-rated-movies', title: 'Highest rated films', kind: 'catalog', type: 'movie', sort: 'rating', limit: 18, enabled: false },
    { id: 'newest-series', title: 'Newest series', kind: 'catalog', type: 'series', sort: 'year', limit: 18, enabled: false },
    { id: 'genre-action', title: 'Action', kind: 'genre', genre: 'Action', limit: 18, enabled: false },
    { id: 'genre-comedy', title: 'Comedy', kind: 'genre', genre: 'Comedy', limit: 18, enabled: false },
    { id: 'genre-drama', title: 'Drama', kind: 'genre', genre: 'Drama', limit: 18, enabled: false },
    { id: 'genre-scifi', title: 'Sci-Fi', kind: 'genre', genre: 'Sci-Fi', limit: 18, enabled: false },
    { id: 'genre-thriller', title: 'Thriller', kind: 'genre', genre: 'Thriller', limit: 18, enabled: false },
    { id: 'genre-documentary', title: 'Documentary', kind: 'genre', genre: 'Documentary', limit: 18, enabled: false },
  ],
  hero: {
    enabled: true,
    rotate: true,
    intervalSeconds: 9,
    source: 'mixed',
    count: 5,
    showDescription: true,
  },
  appearance: {
    accent: '#d8ff3e',
    postersPerRow: 6,
    captionMode: 'focus',
    animations: true,
    showClock: true,
    showServerStatus: true,
    safeArea: 4.5,
    focusScale: 1.1,
    dimSiblings: true,
  },
  playback: {
    seekStep: 10,
    bigSeekStep: 60,
    maxFileSizeGb: 0,
    codecPolicy: 'balanced',
    chromeHideSeconds: 4.5,
  },
}

/* ---------------- persistence ---------------- */

function read(): Partial<AdminConfig> {
  try {
    const raw = localStorage.getItem(STORAGE.config)
    return raw ? (JSON.parse(raw) as Partial<AdminConfig>) : {}
  } catch {
    return {}
  }
}

function clampNumber(value: unknown, min: number, max: number, fallback: number) {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.max(min, Math.min(max, numeric))
}

/**
 * Stored config is merged over the defaults so a new release can add rows and
 * flags without a migration, and a corrupt or hand-edited file cannot produce
 * an unusable interface.
 */
export function mergeConfig(stored: Partial<AdminConfig> | null | undefined): AdminConfig {
  const source = stored && typeof stored === 'object' ? stored : {}

  const storedRows = Array.isArray(source.rows) ? source.rows.filter((row) => row && typeof row.id === 'string') : []
  const order = storedRows.map((row) => row.id)
  const rows = DEFAULT_CONFIG.rows
    .map((base) => {
      const found = storedRows.find((row) => row.id === base.id)
      if (!found) return base
      const title = typeof found.title === 'string' && found.title.trim()
        ? found.title.trim().slice(0, 40)
        : base.title
      return { ...base, ...found, title, kind: base.kind, genre: base.genre, limit: clampNumber(found.limit, 1, 60, base.limit) }
    })
    .sort((a, b) => {
      const ai = order.indexOf(a.id)
      const bi = order.indexOf(b.id)
      return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi)
    })

  const storedNav = Array.isArray(source.nav) ? source.nav.filter((item) => item && SECTION_LABELS[item.id]) : []
  const nav = storedNav.length
    ? storedNav.concat(DEFAULT_CONFIG.nav.filter((base) => !storedNav.some((item) => item.id === base.id)))
    : DEFAULT_CONFIG.nav
  // home must always be reachable, or there is no way back to anything
  const safeNav = nav.map((item) => (item.id === 'home' ? { ...item, enabled: true } : item))

  const appearance = { ...DEFAULT_CONFIG.appearance, ...source.appearance }
  const playback = { ...DEFAULT_CONFIG.playback, ...source.playback }
  const hero = { ...DEFAULT_CONFIG.hero, ...source.hero }

  return {
    version: CONFIG_VERSION,
    pin: typeof source.pin === 'string' ? source.pin.replace(/\D/g, '').slice(0, 8) : DEFAULT_CONFIG.pin,
    features: { ...DEFAULT_CONFIG.features, ...source.features },
    nav: safeNav,
    rows,
    hero: {
      ...hero,
      intervalSeconds: clampNumber(hero.intervalSeconds, 3, 60, DEFAULT_CONFIG.hero.intervalSeconds),
      count: clampNumber(hero.count, 1, 12, DEFAULT_CONFIG.hero.count),
    },
    appearance: {
      ...appearance,
      // 3 or 6 digits only. The 4/5/7/8-digit forms this used to admit parse to
      // rgba(0,0,0,...) in accentAlpha, so the focus glow silently went black
      // while the accent itself looked fine.
      accent: /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(appearance.accent)) ? appearance.accent : DEFAULT_CONFIG.appearance.accent,
      postersPerRow: clampNumber(appearance.postersPerRow, 3, 10, DEFAULT_CONFIG.appearance.postersPerRow),
      safeArea: clampNumber(appearance.safeArea, 1, 10, DEFAULT_CONFIG.appearance.safeArea),
      focusScale: clampNumber(appearance.focusScale, 1, 1.3, DEFAULT_CONFIG.appearance.focusScale),
    },
    playback: {
      ...playback,
      seekStep: clampNumber(playback.seekStep, 5, 60, DEFAULT_CONFIG.playback.seekStep),
      bigSeekStep: clampNumber(playback.bigSeekStep, 15, 600, DEFAULT_CONFIG.playback.bigSeekStep),
      maxFileSizeGb: clampNumber(playback.maxFileSizeGb, 0, 200, DEFAULT_CONFIG.playback.maxFileSizeGb),
      chromeHideSeconds: clampNumber(playback.chromeHideSeconds, 1, 30, DEFAULT_CONFIG.playback.chromeHideSeconds),
    },
  }
}

export const getConfig = (): AdminConfig => mergeConfig(read())

export function saveConfig(config: AdminConfig) {
  const merged = mergeConfig(config)
  try {
    localStorage.setItem(STORAGE.config, JSON.stringify(merged))
  } catch {
    // a full or blocked store must not take the interface down
  }
  return merged
}

export function resetConfig() {
  try {
    localStorage.removeItem(STORAGE.config)
  } catch {
    // ignore
  }
  return mergeConfig(null)
}

/* ---------------- export / import ---------------- */

/**
 * The PIN is deliberately left out. Export and Copy exist to hand a
 * configuration to somebody else, and the PIN is the one field in here whose
 * whole purpose is not being shared. Importing keeps whatever PIN is already
 * set on the receiving TV.
 */
export const exportConfig = (config: AdminConfig) => {
  const { pin: _pin, ...shareable } = config
  return JSON.stringify(shareable, null, 2)
}

export function importConfig(raw: string, current?: AdminConfig): { config?: AdminConfig; error?: string } {
  const trimmed = raw.trim()
  if (!trimmed) return { error: 'Paste a configuration first.' }
  try {
    const parsed = JSON.parse(trimmed) as Partial<AdminConfig>
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { error: 'That is not a configuration object.' }
    }
    // An imported file carries no PIN, so an import must not silently unlock
    // this TV. Preserve the one already set here.
    return { config: mergeConfig({ ...parsed, pin: current?.pin ?? parsed.pin }) }
  } catch {
    return { error: 'That is not valid JSON.' }
  }
}

/* ---------------- derived helpers ---------------- */

export const enabledSections = (config: AdminConfig): SectionId[] =>
  config.nav.filter((item) => item.enabled).map((item) => item.id)

export const enabledRows = (config: AdminConfig): RowConfig[] =>
  config.rows.filter((row) => row.enabled)

export function moveRow(rows: RowConfig[], id: string, delta: number): RowConfig[] {
  const index = rows.findIndex((row) => row.id === id)
  const target = index + delta
  if (index < 0 || target < 0 || target >= rows.length) return rows
  const next = rows.slice()
  const [row] = next.splice(index, 1)
  next.splice(target, 0, row)
  return next
}

export function moveNav(nav: NavItemConfig[], id: SectionId, delta: number): NavItemConfig[] {
  const index = nav.findIndex((item) => item.id === id)
  const target = index + delta
  if (index < 0 || target < 0 || target >= nav.length) return nav
  const next = nav.slice()
  const [item] = next.splice(index, 1)
  next.splice(target, 0, item)
  return next
}

/* ---------------- appearance -> CSS ---------------- */

/** Relative luminance, so label text on the accent stays readable whatever it is set to. */
export function readableInk(hex: string) {
  const value = hex.replace('#', '')
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value.slice(0, 6)
  const channels = [0, 2, 4].map((offset) => Number.parseInt(full.slice(offset, offset + 2), 16) / 255)
  if (channels.some((channel) => !Number.isFinite(channel))) return '#12160a'
  const [r, g, b] = channels.map((channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 0.42 ? '#12160a' : '#ffffff'
}

export function accentAlpha(hex: string, alpha: number) {
  const value = hex.replace('#', '')
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value.slice(0, 6)
  const [r, g, b] = [0, 2, 4].map((offset) => Number.parseInt(full.slice(offset, offset + 2), 16) || 0)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Card width in rem for N posters across the content area of a 120rem canvas. */
export function posterWidthRem(perRow: number, safeArea: number) {
  const gap = 1
  const content = 120 - safeArea * 2
  const count = Math.max(3, Math.min(10, Math.round(perRow)))
  return Math.max(8, Math.round(((content - gap * (count - 1)) / count) * 100) / 100)
}

export function applyAppearance(appearance: AppearanceConfig) {
  const root = document.documentElement
  const width = posterWidthRem(appearance.postersPerRow, appearance.safeArea)
  root.style.setProperty('--accent', appearance.accent)
  root.style.setProperty('--accent-ink', readableInk(appearance.accent))
  root.style.setProperty('--accent-dim', accentAlpha(appearance.accent, 0.14))
  root.style.setProperty('--accent-line', accentAlpha(appearance.accent, 0.34))
  root.style.setProperty('--safe-x', `${appearance.safeArea}rem`)
  root.style.setProperty('--card-w', `${width}rem`)
  root.style.setProperty('--poster-h', `${Math.round(width * 1.5 * 100) / 100}rem`)
  root.style.setProperty('--focus-scale', String(appearance.focusScale))
  root.style.setProperty('--dur', appearance.animations ? '140ms' : '0ms')
  root.setAttribute('data-caption', appearance.captionMode)
  root.setAttribute('data-dim', appearance.dimSiblings ? 'on' : 'off')
}
